import React, { useState, useEffect } from 'react'
import { ArrowLeft, Save, RotateCcw, Upload, X, Paperclip, HelpCircle } from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import supportTicketService from '../../services/supportTicketService'
import userService from '../../services/userService'
import customerMasterService from '../../services/customerMasterService'
import autoIncrementService from '../../services/autoIncrementService'

const STATUS_OPTIONS = ['Open', 'Assigned', 'In Progress', 'Waiting for Customer', 'In Review', 'Resolved', 'Closed', 'Inactive']
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical']
const TYPE_OPTIONS = ['Bug', 'Feature', 'Question', 'Incident', 'Task']
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_FILES = 5

const SupportTicketForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [customers, setCustomers] = useState([])
  const [attachments, setAttachments] = useState([])
  const [ticketId, setTicketId] = useState('')
  const [isLoadingTicketId, setIsLoadingTicketId] = useState(true)
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    status: 'Open',
    priority: 'Medium',
    type: 'Task',
    category: '',
    assignee: '',
    customer: '',
    dueDate: '',
    labels: ''
  })

  useEffect(() => {
    loadOptions()
    fetchTicketId()
  }, [])

  const fetchTicketId = async () => {
    try {
      setIsLoadingTicketId(true)
      const response = await autoIncrementService.getCurrentValue('SUPPORT_TICKET')
      if (response && response.success && response.data) {
        const { data } = response
        if (data.formattedValue) {
          setTicketId(data.formattedValue)
        } else {
          const currentValue = data.value ?? 0
          const paddedValue = currentValue.toString().padStart(4, '0')
          setTicketId(`SUP-${paddedValue}`)
        }
      } else {
        setTicketId('SUP-0001')
      }
    } catch (e) {
      console.error('Error fetching ticket ID:', e)
      setTicketId('SUP-0001')
    } finally {
      setIsLoadingTicketId(false)
    }
  }

  const loadOptions = async () => {
    try {
      const [usersRes, customersRes] = await Promise.all([
        userService.getAllUsers({ limit: 200 }),
        customerMasterService.getAllCustomers({ limit: 500 })
      ])
      if (usersRes?.data) setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.users || [])
      if (customersRes?.success && customersRes?.data)
        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : [])
    } catch (e) {
      console.error('Error loading options:', e)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`)
      return
    }
    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 5MB limit`)
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        setAttachments((prev) => [
          ...prev,
          { fileName: file.name, contentType: file.type, size: file.size, data: reader.result }
        ])
      }
      reader.readAsDataURL(file)
    })
    e.target.value = ''
  }

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleDummyData = () => {
    setFormData((prev) => ({
      ...prev,
      subject: 'Login page not loading on Safari browser',
      description: 'Customers report that the login page hangs indefinitely when accessing from Safari on macOS. Chrome and Firefox work fine. Started happening after last deployment.',
      status: 'Open',
      priority: 'High',
      type: 'Bug',
      category: 'Technical',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      labels: 'browser, login, safari'
    }))
    toast.success('Dummy data populated (ALT+F1)')
  }

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.altKey && (e.key === 'F1' || e.keyCode === 112)) {
        e.preventDefault()
        handleDummyData()
      }
    }
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.subject?.trim()) {
      toast.error('Subject is required')
      return
    }
    try {
      setLoading(true)
      const payload = {
        subject: formData.subject.trim(),
        description: formData.description?.trim() || '',
        status: formData.status,
        priority: formData.priority,
        type: formData.type,
        category: formData.category?.trim() || '',
        assignee: formData.assignee || undefined,
        customer: formData.customer || undefined,
        dueDate: formData.dueDate || undefined,
        labels: formData.labels
          ? formData.labels.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        attachments: attachments.map((a) => ({
          fileName: a.fileName,
          contentType: a.contentType,
          size: a.size,
          url: a.data || a.url
        }))
      }
      const res = await supportTicketService.create(payload)
      if (res.success) {
        toast.success('Support ticket created successfully!')
        navigate('/support/support-tickets')
      } else {
        toast.error(res.message || 'Failed to create ticket')
      }
    } catch (error) {
      console.error('Error creating ticket:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data')
      } else {
        toast.error('Failed to create support ticket')
      }
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Support', path: '/support' },
    { label: 'Support Tickets', path: '/support/support-tickets' },
    { label: 'New Ticket', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>What is Support Ticket?</h3>
      <p>Support Ticket lets you <strong>create a new support request</strong>—subject, description, status, priority, type (e.g. Bug, Feature), category, assignee, customer, due date, labels, and attachments.</p>
      <h3>How to use</h3>
      <p>Enter <strong>Subject</strong> (required) and description. Set priority, type, and optionally assignee, customer, due date. Add attachments if needed. Use <strong>Create Ticket</strong> to save or <strong>Cancel</strong> to go back.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>New Support Ticket</h1>
          <p>Create a new support request</p>
        </div>
      </div>
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Ticket Details</h2>
        </div>
        <form onSubmit={handleSubmit} className="form-content">
          <div className="section-header">
            <h3>Ticket Information</h3>
          </div>
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="ticketId" className="form-label">Ticket ID</label>
              <input
                type="text"
                id="ticketId"
                name="ticketId"
                value={isLoadingTicketId ? 'Generating...' : ticketId}
                readOnly
                className="form-input"
                placeholder="Auto-generated"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 3' }}>
              <label htmlFor="subject" className="form-label required">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="form-input"
                placeholder="Brief summary of the issue"
                required
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="description" className="form-label">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-input"
                rows={4}
                placeholder="Detailed description of the issue"
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="type" className="form-label">Type</label>
              <select id="type" name="type" value={formData.type} onChange={handleChange} className="form-input">
                {TYPE_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="priority" className="form-label">Priority</label>
              <select id="priority" name="priority" value={formData.priority} onChange={handleChange} className="form-input">
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="status" className="form-label">Status</label>
              <select id="status" name="status" value={formData.status} onChange={handleChange} className="form-input">
                {STATUS_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="category" className="form-label">Category</label>
              <input
                type="text"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. Technical, Billing"
              />
            </div>
            <div className="form-group">
              <label htmlFor="assignee" className="form-label">Assignee</label>
              <select id="assignee" name="assignee" value={formData.assignee} onChange={handleChange} className="form-input">
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u._id || u.id} value={u._id || u.id}>
                    {u.name || u.userName || u.userEmail || u.email || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="customer" className="form-label">Customer</label>
              <select id="customer" name="customer" value={formData.customer} onChange={handleChange} className="form-input">
                <option value="">Select customer</option>
                {customers.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.customerName || c.customerCode || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="dueDate" className="form-label">Due Date</label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="labels" className="form-label">Labels (comma-separated)</label>
              <input
                type="text"
                id="labels"
                name="labels"
                value={formData.labels}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. urgent, backend, ui"
              />
            </div>
          </div>

          <div className="section-header" style={{ marginTop: 24 }}>
            <h3><Paperclip size={18} /> Attachments</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Upload Documents</label>
            <div className="file-upload-area">
              <input
                type="file"
                id="fileInput"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
                onChange={handleFileSelect}
                className="file-input-hidden"
              />
              <label htmlFor="fileInput" className="file-upload-label">
                <Upload size={24} />
                <span>Click to upload or drag and drop</span>
                <span className="file-upload-hint">PDF, DOC, XLS, images up to 5MB • Max {MAX_FILES} files</span>
              </label>
            </div>
            {attachments.length > 0 && (
              <ul className="attachment-list">
                {attachments.map((a, idx) => (
                  <li key={idx} className="attachment-item">
                    <Paperclip size={14} />
                    <span>{a.fileName}</span>
                    <span className="attachment-size">({(a.size / 1024).toFixed(1)} KB)</span>
                    <button type="button" className="attachment-remove" onClick={() => removeAttachment(idx)} title="Remove">
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/support/support-tickets')}>
              <ArrowLeft size={16} />
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              <Save size={16} />
              {loading ? 'Saving...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="New Support Ticket – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default SupportTicketForm
